// Supervisor chat: posts the conversation to the supervisor Edge Function with the user's token.
import type { CampaignReach, KnownRule } from "./campaigns";
import type { MetricResult, MetricSpec } from "./performance";
export interface ChatMessage {
  role: "user" | "assistant";
  content: string;
  /** What came with an answer in a past chat (its cards, offer, question, charts, side window), kept with it (3.12). */
  extras?: unknown;
}

/** An answer's extras from a past chat, in the shapes the chat draws (anything else is dropped). */
export function extrasOf(m: ChatMessage): { cards: ChatCard[]; action: ChatAction | null; done: ChatActionDone | null; question: ChatQuestion | null;
  charts: ChatChart[]; side: ChatSide | null } {
  const x = m.extras;
  return { cards: cardsOf(x), action: actionOf(x), done: doneOf(x), question: questionOf(x), charts: chartsOf(x), side: sideOf(x) };
}

/** What an offer did, kept on its answer (act record_ask_action, 1 Nov 2026): a search's id and how many firms it found,
 * or the note shown, so a chat opened again shows it done instead of offering Do it again. */
export type ChatActionDone = { search: string; total: number } | { note: string };

export function doneOf(body: unknown): ChatActionDone | null {
  const d = (body as { done?: unknown })?.done as Record<string, unknown> | null | undefined;
  if (!d || typeof d !== "object") return null;
  if (typeof d.search === "string" && UUID_RE.test(d.search) && typeof d.total === "number" && Number.isInteger(d.total) && d.total >= 0) {
    return { search: d.search, total: d.total };
  }
  return typeof d.note === "string" && d.note.trim() ? { note: d.note.trim() } : null;
}

/** A table the supervisor prepared for download (supervisor lib/tools.ts CsvExport). */
export interface ChatExport {
  filename: string;
  source?: string;
  columns: string[];
  rows: Record<string, unknown>[];
}

/** A chart the supervisor's get_metric drew (supervisor lib/tools.ts ChartOut): the spec can be pinned to Sales
 * performance, which re-runs it for the period the page shows. */
export interface ChatChart {
  title: string;
  spec: MetricSpec;
  result: MetricResult;
  summary: string;
}

/** A card under an answer that opens a page of the app (supervisor lib/tools.ts CardOut): the page to act on, or a firm
 * found (Ask Ogma, Craig 30 Oct 2026). */
export interface ChatCard {
  kind: "page" | "firm";
  title: string;
  detail: string | null;
  to: string;
}

/** Something Ogma offers to do on a confirm card (supervisor lib/tools.ts ActionOut). The screen does it, as the person,
 * only when they press Do it: a search ready to run, a draft campaign, or a campaign paused or resumed. */
export type ChatAction =
  | { kind: "run_search"; title: string; query: string; firms: "both" | "new" | "known" }
  | { kind: "pause_campaign" | "resume_campaign"; title: string; campaign: string }
  | { kind: "set_stage"; title: string; organisation: string; stage: string; reason: string }
  | { kind: "add_note"; title: string; organisation: string; note: string }
  | { kind: "add_todo"; title: string; todo: string; due_on: string | null; organisation: string | null };

/** A campaign drafted with the person (supervisor lib/tools.ts CampaignDraft): shown in the side window; Review and
 * confirm saves it as a draft and opens it. */
export interface CampaignDraft {
  name: string;
  reach: CampaignReach;
  who: string | null;
  where: string | null;
  size: string | null;
  notes: string | null;
  offer: string | null;
  first_ask: string | null;
  known_rule: KnownRule | null;
}

/** An answer that is a thing, opened beside the chat (supervisor lib/tools.ts SideOut). */
export type ChatSide =
  /** spec: the query that made it (asking changes it); null for a list another tool made (quotes). */
  | { kind: "table"; title: string; spec: Record<string, unknown> | null; total: number; file: ChatExport }
  | { kind: "chart"; chart: ChatChart }
  | { kind: "campaign"; draft: CampaignDraft }
  | { kind: "firm"; id: string; items: Record<string, unknown>[] }
  /** A page of the app, opened beside the chat (Craig, 31 Oct 2026): read and acted on with its own buttons. */
  | { kind: "page"; path: string; title: string }
  /** One quote in full (crm_quote). */
  | { kind: "quote"; quote: Record<string, unknown> };

/** One question Ogma asks, with tap answers (supervisor ask_question). */
export interface ChatQuestion {
  text: string;
  options: string[];
}

export type ChatResult =
  | { ok: true; text: string; exports: ChatExport[]; charts: ChatChart[]; cards: ChatCard[]; action: ChatAction | null; conversation: string | null;
      side: ChatSide | null; question: ChatQuestion | null }
  | { ok: false; reason: "not_deployed" | "signed_out" | "failed"; text: string };

/** The charts in a reply, kept only when they have the shape MetricChart draws. */
export function chartsOf(body: unknown): ChatChart[] {
  const list = (body as { charts?: unknown })?.charts;
  if (!Array.isArray(list)) return [];
  return list
    .filter((c): c is ChatChart => {
      const x = c as ChatChart;
      return Boolean(x) && typeof x.title === "string" && Boolean(x.spec) && typeof x.spec.metric === "string" && Boolean(x.result) && Array.isArray(x.result.rows);
    })
    .slice(0, 4);
}

/** A path of this app (never a web address, never another site). */
export const isAppPath = (v: unknown): v is string => typeof v === "string" && /^\/[a-z]/.test(v) && !v.startsWith("//") && !/[\s\\]/.test(v);

/** The cards in a reply, kept only when each opens a page of this app (a path, never a web address). */
export function cardsOf(body: unknown): ChatCard[] {
  const list = (body as { cards?: unknown })?.cards;
  if (!Array.isArray(list)) return [];
  return list
    .filter((c): c is ChatCard => {
      const x = c as ChatCard;
      return Boolean(x) && (x.kind === "page" || x.kind === "firm") && typeof x.title === "string" && isAppPath(x.to);
    })
    .slice(0, 8);
}

/** The page the person asks from: its path, and the campaign or firm in view (only ids, never names). */
export interface AskContext {
  path: string;
  campaign: string | null;
  organisation: string | null;
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** The context of an address: /contacts/companies/<id> names a firm, ?campaign=<id> a campaign. */
export function askContext(pathname: string, search: string): AskContext {
  const firm = /^\/contacts\/companies\/([^/]+)$/.exec(pathname)?.[1] ?? null;
  const campaign = new URLSearchParams(search).get("campaign");
  return {
    path: firm ? "/contacts/companies" : pathname.replace(/\/+$/, "") || "/ask",
    campaign: campaign && UUID_RE.test(campaign) ? campaign : null,
    organisation: firm && UUID_RE.test(firm) ? firm : null,
  };
}

const conversationOf = (body: unknown): string | null => {
  const v = (body as { conversation?: unknown })?.conversation;
  return typeof v === "string" && UUID_RE.test(v) ? v : null;
};

/** One of the person's past conversations (private to them, 90 days; migration 20270228160000). */
export interface PastConversation {
  id: string;
  title: string;
  updated_at: string;
  /** How many messages, and the start of the last answer (migration 20270308090000; absent before it). */
  messages?: number;
  last_answer?: string | null;
}

/** The offered action, kept only when it is one of the four the screen knows and has what it needs. */
export function actionOf(body: unknown): ChatAction | null {
  const a = (body as { action?: unknown })?.action as Record<string, unknown> | null | undefined;
  if (!a || typeof a !== "object" || typeof a.title !== "string") return null;
  const str = (v: unknown) => (typeof v === "string" && v.trim() ? v.trim() : null);
  if (a.kind === "run_search" && str(a.query)) return { kind: "run_search", title: a.title, query: str(a.query)!, firms: a.firms === "new" || a.firms === "known" ? a.firms : "both" };
  if ((a.kind === "pause_campaign" || a.kind === "resume_campaign") && str(a.campaign)) return { kind: a.kind, title: a.title, campaign: str(a.campaign)! };
  const org = typeof a.organisation === "string" && UUID_RE.test(a.organisation) ? a.organisation : null;
  if (a.kind === "set_stage" && org && str(a.stage) && /^[a-z][a-z0-9_]{0,39}$/.test(String(a.stage))) {
    return { kind: "set_stage", title: a.title, organisation: org, stage: str(a.stage)!, reason: str(a.reason) ?? a.title };
  }
  if (a.kind === "add_note" && org && str(a.note)) return { kind: "add_note", title: a.title, organisation: org, note: str(a.note)! };
  if (a.kind === "add_todo" && str(a.todo)) {
    const due = typeof a.due_on === "string" && /^\d{4}-\d{2}-\d{2}$/.test(a.due_on) ? a.due_on : null;
    return { kind: "add_todo", title: a.title, todo: str(a.todo)!, due_on: due, organisation: org };
  }
  return null;
}

const isObj = (v: unknown): v is Record<string, unknown> => typeof v === "object" && v !== null && !Array.isArray(v);
const isExport = (e: unknown): e is ChatExport => isObj(e) && typeof e.filename === "string" && Array.isArray(e.columns) && Array.isArray(e.rows);
const REACHES: CampaignReach[] = ["new", "known", "both"];
const isReach = (v: unknown): v is CampaignReach => REACHES.some((r) => r === v);

const text = (v: unknown) => (typeof v === "string" && v.trim() ? v.trim() : null);
const whole = (v: unknown, lo: number, hi: number, dflt: number) => (typeof v === "number" && Number.isInteger(v) && v >= lo && v <= hi ? v : dflt);

function ruleOf(v: unknown): KnownRule | null {
  if (!isObj(v)) return null;
  const f = isObj(v.firms) ? v.firms : {};
  const kind = f.kind === "stage" || f.kind === "tag" || f.kind === "tier" ? f.kind : "all";
  const value = text(f.value);
  const topic = text(v.topic);
  return {
    quiet_days: whole(v.quiet_days, 7, 730, 60),
    firms: kind === "all" || !value ? { kind: "all" } : { kind, value },
    ...(topic ? { topic } : {}),
    daily_limit: whole(v.daily_limit, 1, 100, 10),
  };
}

/** A drafted campaign, read field by field. */
export function draftOf(v: unknown): CampaignDraft | null {
  if (!isObj(v) || !text(v.name) || !isReach(v.reach)) return null;
  const reach = v.reach;
  return {
    name: text(v.name)!, reach, who: text(v.who), where: text(v.where), size: text(v.size), notes: text(v.notes), offer: text(v.offer),
    first_ask: text(v.first_ask), known_rule: reach === "new" ? null : ruleOf(v.known_rule) ?? { quiet_days: 60, firms: { kind: "all" }, daily_limit: 10 },
  };
}

/** The thing for the side window, kept only in a shape the screen draws. */
export function sideOf(body: unknown): ChatSide | null {
  const s = (body as { side?: unknown })?.side;
  if (!isObj(s)) return null;
  if (s.kind === "table" && typeof s.title === "string" && (isObj(s.spec) || s.spec === null) && typeof s.total === "number" && isExport(s.file)) {
    return { kind: "table", title: s.title, spec: s.spec, total: s.total, file: s.file };
  }
  if (s.kind === "page" && typeof s.title === "string" && isAppPath(s.path)) return { kind: "page", path: s.path, title: s.title };
  if (s.kind === "quote" && isObj(s.quote) && typeof s.quote.id === "string" && UUID_RE.test(s.quote.id)) return { kind: "quote", quote: s.quote };
  if (s.kind === "chart") {
    const [chart] = chartsOf({ charts: [s.chart] });
    return chart ? { kind: "chart", chart } : null;
  }
  if (s.kind === "campaign") {
    const draft = draftOf(s.draft);
    return draft ? { kind: "campaign", draft } : null;
  }
  if (s.kind === "firm" && typeof s.id === "string" && UUID_RE.test(s.id) && Array.isArray(s.items)) {
    return { kind: "firm", id: s.id, items: s.items.filter(isObj) };
  }
  return null;
}

/** The question asked, with its tap answers (at most five). */
export function questionOf(body: unknown): ChatQuestion | null {
  const q = (body as { question?: unknown })?.question;
  if (!isObj(q) || typeof q.text !== "string" || !q.text.trim()) return null;
  const options = Array.isArray(q.options) ? q.options.filter((o): o is string => typeof o === "string" && o.trim() !== "").slice(0, 5) : [];
  return { text: q.text, options };
}

/** What the side window shows, as the supervisor takes it back (so "only the ones in Scotland" changes it). */
export function sideForRequest(s: ChatSide | null): Record<string, unknown> | null {
  if (!s) return null;
  if (s.kind === "table") return s.spec ? { kind: "table", spec: s.spec } : { kind: "shown", title: s.title };
  if (s.kind === "page") return { kind: "page", path: s.path };
  if (s.kind === "quote") return { kind: "quote", id: s.quote.id };
  if (s.kind === "chart") return { kind: "chart", metric: s.chart.spec.metric, title: s.chart.title };
  if (s.kind === "campaign") return { kind: "campaign", draft: s.draft };
  return { kind: "firm", id: s.id };
}

function exportsOf(body: unknown): ChatExport[] {
  const list = (body as { exports?: unknown })?.exports;
  if (!Array.isArray(list)) return [];
  return list.filter(isExport);
}

/** Pulls the reply text out of the shapes the supervisor may return. */
export function replyText(body: unknown): string | null {
  if (typeof body === "string") return body || null;
  if (!body || typeof body !== "object") return null;
  const b = body as Record<string, unknown>;
  for (const k of ["reply", "text", "message", "content", "answer"]) {
    const v = b[k];
    if (typeof v === "string" && v) return v;
    if (Array.isArray(v)) {
      const t = v.map((p) => (typeof p === "string" ? p : (p as { text?: string })?.text ?? "")).join("");
      if (t) return t;
    }
    if (v && typeof v === "object") {
      const inner = replyText(v);
      if (inner) return inner;
    }
  }
  return null;
}

export async function askSupervisor(opts: {
  url: string;
  token: string;
  anonKey: string;
  messages: ChatMessage[];
  /** Where the person asks from, and the conversation being continued (Ask Ogma everywhere, 30 Oct 2026). */
  context?: AskContext | null;
  conversation?: string | null;
  /** What the side window shows now. */
  side?: ChatSide | null;
  fetchImpl?: typeof fetch;
}): Promise<ChatResult> {
  const f = opts.fetchImpl ?? fetch;
  let res: Response;
  try {
    res = await f(opts.url, {
      method: "POST",
      headers: { "content-type": "application/json", authorization: `Bearer ${opts.token}`, apikey: opts.anonKey },
      // The words only: an answer's extras stay in the history, never go back to the model
      body: JSON.stringify({ messages: opts.messages.map(({ role, content }) => ({ role, content })), ...(opts.context ? { context: opts.context } : {}), ...(opts.conversation ? { conversation: opts.conversation } : {}),
        ...(opts.side ? { side: sideForRequest(opts.side) } : {}) }),
    });
  } catch {
    return { ok: false, reason: "not_deployed", text: "The assistant can't be reached. It may not be switched on yet; everything else on this page still works." };
  }
  if (res.status === 404) return { ok: false, reason: "not_deployed", text: "The assistant isn't switched on yet. Ogma's team is setting it up; everything else on this page still works." };
  if (res.status === 503) return { ok: false, reason: "not_deployed", text: "The assistant isn't switched on yet (it has no Claude API key). Everything else on this page still works." };
  if (res.status === 401) return { ok: false, reason: "signed_out", text: "Your session has ended. Sign in again to keep chatting." };
  let body: unknown = null;
  const type = res.headers.get("content-type") ?? "";
  try {
    body = type.includes("json") ? await res.json() : await res.text();
  } catch {
    body = null;
  }
  if (!res.ok) {
    const detail = replyText(body as object) ?? (body as { error?: string })?.error;
    return { ok: false, reason: "failed", text: detail ? `The assistant couldn't answer: ${detail}` : "The assistant couldn't answer. Try again in a moment." };
  }
  const side = sideOf(body);
  const cards = cardsOf(body);
  const action = actionOf(body);
  const question = questionOf(body);
  // Something to show is an answer even without words (31 Oct 2026: a table found was thrown away as "empty")
  const text = replyText(body) ?? (side || cards.length || action || question ? "" : null);
  return text !== null ? { ok: true, text, exports: exportsOf(body), charts: chartsOf(body), cards, action, conversation: conversationOf(body),
    side, question } : { ok: false, reason: "failed", text: "The assistant sent an empty reply. Try asking another way." };
}
